import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IFifthWheelHistory, defaultValue } from 'app/shared/model/fifth-wheel-history.model';

const initialState: EntityState<IFifthWheelHistory> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/fifth-wheel-histories';

interface IFifthWheelHistoryParams extends IQueryParams {
  truckImplementSearch?: string;
  dateSearch?: string;
  companyId?: number;
}

// Actions

export const getEntities = createAsyncThunk(
  'fifthWheelHistory/fetch_entity_list',
  async ({ dateSearch, truckImplementSearch, companyId, page, size, sort }: IFifthWheelHistoryParams) => {
    const onDate = dateSearch ? `&date=${dateSearch}` : '';
    const onTruck = truckImplementSearch ? `&truckImplementSearch=${truckImplementSearch}` : '';
    const onCompanyId = companyId ? `&companyId=${companyId}` : '';
    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}${onCompanyId}${onTruck}${onDate}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IFifthWheelHistory[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'fifthWheelHistory/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IFifthWheelHistory>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'fifthWheelHistory/create_entity',
  async (entity: IFifthWheelHistory, thunkAPI) => {
    const result = await axios.post<IFifthWheelHistory>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'fifthWheelHistory/update_entity',
  async (entity: IFifthWheelHistory, thunkAPI) => {
    const result = await axios.put<IFifthWheelHistory>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'fifthWheelHistory/partial_update_entity',
  async (entity: IFifthWheelHistory, thunkAPI) => {
    const result = await axios.patch<IFifthWheelHistory>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'fifthWheelHistory/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IFifthWheelHistory>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const FifthWheelHistorySlice = createEntitySlice({
  name: 'fifthWheelHistory',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = FifthWheelHistorySlice.actions;

// Reducer
export default FifthWheelHistorySlice.reducer;
